import { useState } from 'react';
import { Activity, Brain, RefreshCw, Zap, Sliders, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const EbbinghausSimulator = () => {
  const [strengthS, setStrengthS] = useState(15); // Memory Strength S (days)
  const [elapsedT, setElapsedT] = useState(7);    // Elapsed Time t (days)
  const [mode, setMode] = useState('biological');   // 'biological' | 'llm'
  const [showExplanation, setShowExplanation] = useState(false);

  // Ebbinghaus formula: R(t) = e^(-t / S)
  const calculateRetention = (t, S) => {
    return Math.exp(-t / S);
  };

  const currentRetention = calculateRetention(elapsedT, strengthS);
  const currentPercentage = (currentRetention * 100).toFixed(1);

  // SVG Chart Dimensions
  const svgWidth = 580;
  const svgHeight = 220;
  const paddingX = 50;
  const paddingY = 30;
  const graphWidth = svgWidth - paddingX * 2;
  const graphHeight = svgHeight - paddingY * 2;

  // Generate SVG path for 0 to 60 days
  const points = [];
  for (let day = 0; day <= 60; day += 0.5) {
    const ret = calculateRetention(day, strengthS);
    const x = paddingX + (day / 60) * graphWidth;
    const y = paddingY + graphHeight - ret * graphHeight;
    points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }

  const pathD = `M ${points.join(' L ')}`;

  // Calculate current indicator point (x, y)
  const currentX = paddingX + (elapsedT / 60) * graphWidth;
  const currentY = paddingY + graphHeight - currentRetention * graphHeight;

  // Comparative LLM L2 regularization curve
  const lambda = 1 - Math.exp(-1 / (strengthS * 0.4));
  const llmPoints = [];
  for (let day = 0; day <= 60; day += 0.5) {
    const ret = Math.pow(1 - lambda, day);
    const x = paddingX + (day / 60) * graphWidth;
    const y = paddingY + graphHeight - ret * graphHeight;
    llmPoints.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  const llmPathD = `M ${llmPoints.join(' L ')}`;

  const getPhaseLabel = (retentionVal) => {
    if (retentionVal > 0.7) return { text: 'High Retrievability', badge: 'bg-emerald-950/70 border-emerald-500/50 text-emerald-300 shadow-emerald-500/20' };
    if (retentionVal > 0.4) return { text: 'Trace Decay Onset', badge: 'bg-purple-950/70 border-purple-500/50 text-purple-300 shadow-purple-500/20' };
    if (retentionVal > 0.15) return { text: 'Retrieval Failure Zone', badge: 'bg-amber-950/70 border-amber-500/50 text-amber-300 shadow-amber-500/20' };
    return { text: 'Sub-threshold (Requires Relearning)', badge: 'bg-rose-950/70 border-rose-500/50 text-rose-300 shadow-rose-500/20' };
  };

  const phaseInfo = getPhaseLabel(currentRetention);

  return (
    <div className="bg-[#090714] backdrop-blur-xl border border-purple-500/30 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl relative overflow-hidden my-8">
      {/* Glass Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-purple-500/20 pb-5 relative z-10">
        <div className="flex items-center gap-3.5">
          <div className="p-3 rounded-2xl bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 shadow-lg shadow-purple-500/20">
            <Activity className="w-5 h-5 animate-pulse text-fuchsia-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-mono font-bold px-2.5 py-0.5 bg-purple-950/80 text-fuchsia-300 border border-purple-500/40 rounded-full inline-flex items-center gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 animate-pulse" />
                [ INTERACTIVE // SIMULATOR ]
              </span>
            </div>
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-white tracking-wide leading-tight">
              Ebbinghaus Forgetting Curve Simulator
            </h3>
            <p className="text-xs font-mono text-gray-400 mt-1">
              Decay Equation: <code className="text-fuchsia-300 font-bold bg-[#0d0a1c] px-1.5 py-0.5 rounded border border-purple-500/30">R(t) = e^(-t / S)</code>
            </p>
          </div>
        </div>

        {/* Mode Switcher Buttons */}
        <div className="flex items-center gap-1.5 bg-[#0d0a1c] p-1.5 rounded-2xl border border-purple-500/20 self-start sm:self-auto font-mono text-xs shadow-inner">
          <button
            onClick={() => setMode('biological')}
            className={`px-3.5 py-1.5 rounded-xl font-bold transition-all duration-300 flex items-center gap-1.5 ${
              mode === 'biological'
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border border-purple-400/30 text-white shadow-lg shadow-purple-600/30 scale-[1.02]'
                : 'text-gray-400 hover:text-fuchsia-300'
            }`}
          >
            <Brain className="w-3.5 h-3.5" /> Bio Decay
          </button>
          <button
            onClick={() => setMode('llm')}
            className={`px-3.5 py-1.5 rounded-xl font-bold transition-all duration-300 flex items-center gap-1.5 ${
              mode === 'llm'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 border border-pink-400/30 text-white shadow-lg shadow-pink-600/30 scale-[1.02]'
                : 'text-gray-400 hover:text-pink-300'
            }`}
          >
            <Zap className="w-3.5 h-3.5" /> LLM Comparison
          </button>
        </div>
      </div>

      {/* SVG Interactive Visualizer Box */}
      <div className="bg-[#0d0a1c] border border-purple-500/20 rounded-2xl p-4 sm:p-6 shadow-2xl relative z-10 space-y-4">
        {/* Top Status & Legend Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-mono border-b border-purple-500/20 pb-3.5">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="w-3 h-1.5 bg-gradient-to-r from-purple-400 to-fuchsia-400 rounded-full shadow-[0_0_8px_rgba(232,121,249,0.6)]" />
              <span className="text-gray-200 font-medium">Ebbinghaus Memory Curve</span>
            </div>
            {mode === 'llm' && (
              <div className="flex items-center gap-2">
                <span className="w-3 h-1.5 bg-pink-400 rounded-full" />
                <span className="text-pink-300">Uniform L2 Weight Decay</span>
              </div>
            )}
          </div>

          <span className={`px-3 py-1 rounded-full border text-[11px] font-bold shadow-md transition-all ${phaseInfo.badge}`}>
            {phaseInfo.text}
          </span>
        </div>

        {/* SVG Plotter Frame */}
        <div className="relative w-full overflow-hidden pt-1">
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="w-full h-auto overflow-visible"
          >
            <defs>
              {/* Curve Area Fill Gradient */}
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#e879f9" stopOpacity="0.35" />
                <stop offset="60%" stopColor="#a855f7" stopOpacity="0.1" />
                <stop offset="100%" stopColor="#0d0a1c" stopOpacity="0.0" />
              </linearGradient>

              {/* Curve Line Stroke Glow */}
              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3.5" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {/* Horizontal Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
              const y = paddingY + graphHeight * (1 - ratio);
              return (
                <g key={idx}>
                  <line
                    x1={paddingX}
                    y1={y}
                    x2={svgWidth - paddingX}
                    y2={y}
                    stroke="#2e1065"
                    strokeDasharray="4,4"
                    opacity="0.4"
                  />
                  <text
                    x={paddingX - 10}
                    y={y + 3.5}
                    fill="#9ca3af"
                    fontSize="10"
                    fontFamily="monospace"
                    textAnchor="end"
                  >
                    {`${Math.round(ratio * 100)}%`}
                  </text>
                </g>
              );
            })}

            {/* X-axis ticks (Days 0, 15, 30, 45, 60) */}
            {[0, 15, 30, 45, 60].map((day, idx) => {
              const x = paddingX + (day / 60) * graphWidth;
              return (
                <g key={idx}>
                  <line
                    x1={x}
                    y1={svgHeight - paddingY}
                    x2={x}
                    y2={svgHeight - paddingY + 5}
                    stroke="#4b5563"
                  />
                  <text
                    x={x}
                    y={svgHeight - paddingY + 20}
                    fill="#9ca3af"
                    fontSize="10"
                    fontFamily="monospace"
                    textAnchor="middle"
                  >
                    {`t=${day}d`}
                  </text>
                </g>
              );
            })}

            {/* Gradient Fill under curve */}
            <path
              d={`${pathD} L ${paddingX + graphWidth},${paddingY + graphHeight} L ${paddingX},${paddingY + graphHeight} Z`}
              fill="url(#areaGradient)"
            />

            {/* LLM Comparison line (if enabled) */}
            {mode === 'llm' && (
              <path
                d={llmPathD}
                fill="none"
                stroke="#f472b6"
                strokeWidth="2.5"
                strokeDasharray="6,6"
                opacity="0.85"
              />
            )}

            {/* Main Ebbinghaus Curve Path with Glow */}
            <path
              d={pathD}
              fill="none"
              stroke="#e879f9"
              strokeWidth="3.5"
              strokeLinecap="round"
              filter="url(#glow)"
            />

            {/* Reticle Guidelines to Axes */}
            <line
              x1={paddingX}
              y1={currentY}
              x2={currentX}
              y2={currentY}
              stroke="#f472b6"
              strokeWidth="1"
              strokeDasharray="2,2"
              opacity="0.6"
            />
            <line
              x1={currentX}
              y1={currentY}
              x2={currentX}
              y2={svgHeight - paddingY}
              stroke="#f472b6"
              strokeWidth="1"
              strokeDasharray="2,2"
              opacity="0.6"
            />

            {/* Target Reticle Indicator Pin */}
            <circle
              cx={currentX}
              cy={currentY}
              r="7"
              fill="#e879f9"
              stroke="#ffffff"
              strokeWidth="2"
              className="shadow-lg"
            />
            <circle
              cx={currentX}
              cy={currentY}
              r="14"
              fill="#e879f9"
              opacity="0.3"
              className="animate-ping"
            />
          </svg>
        </div>

        {/* Live Calculation Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 pt-2 font-mono text-xs">
          <div className="bg-[#090714] p-3.5 rounded-2xl border border-purple-500/20 shadow-md flex flex-col justify-between hover:border-purple-500/40 transition-colors">
            <span className="text-gray-400 text-[10px] uppercase tracking-wider">[ 01 // TRACE STRENGTH S ]</span>
            <span className="text-fuchsia-300 font-bold text-lg mt-1">{strengthS} Days</span>
            <span className="text-[10px] text-fuchsia-400/80">Days to reach 36.8% retention (e⁻¹)</span>
          </div>

          <div className="bg-[#090714] p-3.5 rounded-2xl border border-purple-500/20 shadow-md flex flex-col justify-between hover:border-pink-500/40 transition-colors">
            <span className="text-gray-400 text-[10px] uppercase tracking-wider">[ 02 // ELAPSED TIME t ]</span>
            <span className="text-pink-300 font-bold text-lg mt-1">{elapsedT} Days</span>
            <span className="text-[10px] text-pink-400/80">Days passed post-learning</span>
          </div>

          <div className="bg-gradient-to-br from-purple-950/60 to-pink-950/50 p-3.5 rounded-2xl border border-purple-500/40 shadow-lg flex flex-col justify-between">
            <span className="text-purple-300 text-[10px] uppercase tracking-wider font-bold">[ 03 // RETENTION R(t) ]</span>
            <span className="text-fuchsia-300 font-black text-xl mt-0.5 drop-shadow-[0_0_10px_rgba(232,121,249,0.4)]">{currentPercentage}%</span>
            <span className="text-[10px] text-fuchsia-300/80">R({elapsedT}) = e^(-{elapsedT}/{strengthS})</span>
          </div>
        </div>
      </div>

      {/* Sliders & Parameter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10 pt-1 font-mono text-xs">
        {/* Slider 1: Memory Strength (S) */}
        <div className="bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20 space-y-3 shadow-lg hover:border-purple-500/40 transition-colors">
          <div className="flex items-center justify-between">
            <label className="text-gray-200 font-bold flex items-center gap-2">
              <Sliders className="w-4 h-4 text-fuchsia-400" /> Memory Strength (S)
            </label>
            <span className="text-fuchsia-300 bg-purple-950/80 px-2.5 py-1 rounded-lg border border-purple-500/40 font-bold text-xs shadow-md">
              {strengthS} days
            </span>
          </div>
          <input
            type="range"
            min="1"
            max="50"
            value={strengthS}
            onChange={(e) => setStrengthS(Number(e.target.value))}
            className="w-full h-2 bg-purple-950 rounded-lg appearance-none cursor-pointer accent-fuchsia-500"
          />
          <p className="text-[11px] text-gray-400 font-sans leading-relaxed">
            <strong className="text-gray-200">What S means:</strong> <strong className="text-fuchsia-300">S</strong> is the stability time constant (in days). At <strong className="text-gray-200">t = S days</strong>, retention drops to exactly <strong className="text-fuchsia-300">36.8% (1/e)</strong>. Higher <strong className="text-gray-200">S</strong> values simulate consolidated memory from spaced repetition or sleep.
          </p>
        </div>

        {/* Slider 2: Elapsed Time (t) */}
        <div className="bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20 space-y-3 shadow-lg hover:border-pink-500/40 transition-colors">
          <div className="flex items-center justify-between">
            <label className="text-gray-200 font-bold flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-pink-400" /> Time Elapsed (t)
            </label>
            <span className="text-pink-300 bg-pink-950/80 px-2.5 py-1 rounded-lg border border-pink-500/40 font-bold text-xs shadow-md">
              {elapsedT} days
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="60"
            value={elapsedT}
            onChange={(e) => setElapsedT(Number(e.target.value))}
            className="w-full h-2 bg-purple-950 rounded-lg appearance-none cursor-pointer accent-pink-500"
          />
          <p className="text-[11px] text-gray-400 font-sans leading-relaxed">
            Measures time passed post-acquisition. Notice how retention drops steepest in the early days before leveling off.
          </p>
        </div>
      </div>

      {/* Expandable Explanation Guide Accordion */}
      <div className="pt-2 relative z-10">
        <button
          onClick={() => setShowExplanation(!showExplanation)}
          className="w-full bg-[#0d0a1c] hover:bg-[#120e26] border border-purple-500/30 hover:border-purple-500/60 p-4 rounded-2xl flex items-center justify-between text-xs font-mono text-gray-200 transition-all duration-300 shadow-xl group"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-purple-500/20 text-fuchsia-300 border border-purple-500/30 group-hover:scale-110 transition-transform">
              <HelpCircle className="w-4 h-4" />
            </div>
            <span className="font-serif font-bold text-white text-sm">What does this demonstration show?</span>
            <span className="text-[10px] text-fuchsia-300 bg-purple-950/80 px-2.5 py-1 rounded-full border border-purple-500/40 hidden sm:inline-block font-mono">
              [ THEORETICAL // BREAKDOWN ]
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-gray-400 group-hover:text-white font-mono text-xs">
            <span>{showExplanation ? 'Hide Guide' : 'Expand Guide'}</span>
            {showExplanation ? (
              <ChevronUp className="w-4 h-4 text-fuchsia-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-fuchsia-400" />
            )}
          </div>
        </button>

        {showExplanation && (
          <div className="mt-3.5 bg-[#0d0a1c] border border-purple-500/30 p-6 rounded-2xl text-xs space-y-5 text-gray-300 font-sans animate-fade-in shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-sans relative z-10">
              {/* Concept 1 */}
              <div className="bg-[#090714] p-4 rounded-2xl border border-purple-500/20 space-y-2 shadow-md hover:border-fuchsia-500/40 transition-colors">
                <div className="text-fuchsia-300 font-bold text-xs font-mono">
                  [ 01 // THE FORGETTING CURVE ]
                </div>
                <p className="text-xs text-gray-300 leading-relaxed font-mono">
                  Memory retention drops exponentially over time. Without review or reinforcement, the fastest memory loss occurs within the first 24–48 hours post-acquisition.
                </p>
              </div>

              {/* Concept 2 */}
              <div className="bg-[#090714] p-4 rounded-2xl border border-purple-500/20 space-y-2 shadow-md hover:border-pink-500/40 transition-colors">
                <div className="text-pink-300 font-bold text-xs font-mono">
                  [ 02 // TRACE STRENGTH S ]
                </div>
                <p className="text-xs text-gray-300 leading-relaxed font-mono">
                  <strong className="text-gray-100">S</strong> measures memory durability in <strong className="text-pink-300">Days</strong>. Mathematically, when time <strong className="text-gray-100">t = S days</strong>, retention drops to <strong className="text-pink-300">36.8% (1/e)</strong>.
                </p>
              </div>

              {/* Concept 3 */}
              <div className="bg-[#090714] p-4 rounded-2xl border border-purple-500/20 space-y-2 shadow-md hover:border-purple-400/40 transition-colors">
                <div className="text-purple-300 font-bold text-xs font-mono">
                  [ 03 // BIO VS AI WEIGHT DECAY ]
                </div>
                <p className="text-xs text-gray-300 leading-relaxed font-mono">
                  Standard AI regularization (L2) decays all parameters uniformly, causing catastrophic forgetting. Biological Ebbinghaus decay selectively prunes unused noise while safeguarding core synaptic weights.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EbbinghausSimulator;
